'use client';

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppLogo from '@/components/ui/AppLogo';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'OWNER\' as \'OWNER\' | \'PROFESSIONAL',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredRole, setRegisteredRole] = useState<'OWNER' | 'PROFESSIONAL'>('OWNER');
  const { signUp } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await signUp(formData.email, formData.password, {
        fullName: formData.fullName,
        role: formData.role,
      });

      setRegisteredRole(formData.role);
      setRegistrationComplete(true);
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  // Show email verification pending screen after registration
  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 dark:from-[#0f172a] dark:via-[#0f172a] dark:to-[#0f172a] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link href="/" className="inline-flex items-center gap-2.5 group">
              <AppLogo
                src="/assets/images/Logo_big-1771795433731.png"
                size={48}
                text="DogFob Connect"
                className="group-hover:scale-105 transition-transform duration-200"
              />
            </Link>
          </div>
          <div className="feature-card p-8 text-center">
            <div className="w-16 h-16 bg-teal-100 dark:bg-teal-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="EnvelopeIcon" size={32} className="text-teal-600 dark:text-teal-400" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">Check your email</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              We&apos;ve sent a verification link to <strong className="text-slate-800 dark:text-slate-200">{formData.email}</strong>.
              Please verify your email to activate your account.
            </p>

            {registeredRole === 'PROFESSIONAL' && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4 mb-4 text-left">
                <div className="flex items-start gap-3">
                  <Icon name="InformationCircleIcon" size={20} className="text-blue-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">Professional account next steps</p>
                    <ol className="text-sm text-blue-700 dark:text-blue-400 mt-1 space-y-1 list-decimal list-inside">
                      <li>Verify your email address</li>
                      <li>Complete your business profile</li>
                      <li>Await admin verification approval</li>
                    </ol>
                  </div>
                </div>
              </div>
            )}

            <p className="text-sm text-slate-500 dark:text-slate-500 mb-6">
              Didn&apos;t receive the email? Check your spam folder or{' '}
              <Link href="/login" className="text-teal-600 dark:text-teal-400 font-semibold hover:text-teal-700">
                sign in to resend
              </Link>.
            </p>

            <Link href="/login" className="btn-primary w-full py-3 flex items-center justify-center gap-2">
              <Icon name="ArrowRightOnRectangleIcon" size={20} />
              Go to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 dark:from-[#0f172a] dark:via-[#0f172a] dark:to-[#0f172a] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link href="/" className="inline-flex items-center gap-2.5 group">
            <AppLogo
              src="/assets/images/Logo_big-1771795433731.png"
              size={48}
              text="DogFob Connect"
              className="group-hover:scale-105 transition-transform duration-200"
            />
          </Link>
          <p className="mt-3 text-slate-600 dark:text-slate-400">Create your account</p>
        </div>

        {/* Register Card */}
        <div className="feature-card p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl flex items-start gap-2">
                <Icon name="ExclamationCircleIcon" size={20} className="text-red-500 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Role Selection */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">I am a:</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'OWNER' })}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.role === 'OWNER' ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30' : 'border-slate-200 dark:border-slate-600 hover:border-teal-300 dark:bg-slate-800'
                  }`}
                >
                  <Icon
                    name="UserIcon"
                    size={24}
                    className={`mx-auto mb-2 ${formData.role === 'OWNER' ? 'text-teal-600 dark:text-teal-400' : 'text-slate-400 dark:text-slate-500'}`}
                  />
                  <div className={`text-sm font-semibold ${formData.role === 'OWNER' ? 'text-teal-700 dark:text-teal-300' : 'text-slate-700 dark:text-slate-300'}`}>Pet Owner</div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'PROFESSIONAL' })}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.role === 'PROFESSIONAL' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'border-slate-200 dark:border-slate-600 hover:border-blue-300 dark:bg-slate-800'
                  }`}
                >
                  <Icon
                    name="BriefcaseIcon"
                    size={24}
                    className={`mx-auto mb-2 ${formData.role === 'PROFESSIONAL' ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400 dark:text-slate-500'}`}
                  />
                  <div className={`text-sm font-semibold ${formData.role === 'PROFESSIONAL' ? 'text-blue-700 dark:text-blue-300' : 'text-slate-700 dark:text-slate-300'}`}>Professional</div>
                </button>
              </div>
              {formData.role === 'PROFESSIONAL' && (
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 flex items-center gap-1">
                  <Icon name="InformationCircleIcon" size={14} />
                  Professional accounts require email verification and admin approval.
                </p>
              )}
            </div>

            <div>
              <label htmlFor="fullName" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Full Name
              </label>
              <input
                id="fullName"
                type="text"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="form-input w-full"
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="form-input w-full"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="form-input w-full"
                placeholder="••••••••"
                required
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="form-input w-full"
                placeholder="••••••••"
                required
              />
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full py-3">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  Creating account...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Icon name="UserPlusIcon" size={20} />
                  Create Account
                </span>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Already have an account?{' '}
              <Link href="/login" className="text-teal-600 dark:text-teal-400 font-semibold hover:text-teal-700">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}